/**
 * The region's weekly goal in the idle game (D244). Kept apart from `idle.json` so the
 * server's numbers and the world's numbers can change without touching each other.
 *
 * The target for a week is sized per world (D255): each world that reports that week adds
 * `goalOfLastWeek` times what it earned the week before, never less than `goalFloor`, which is
 * what a new world's part starts at. Meeting it pays every member world a value multiplier of
 * `bonusFactor` for the whole of the next week.
 */
import { z } from 'zod';
import idleRegion from '../content/idle_region.json' with { type: 'json' };

export const IdleRegionContentSchema = z
  .object({
    /** The least each world's part of a week's target can be: a new world's first part (D255). */
    goalFloor: z.number().positive().finite(),
    /** Each world's part is this times what it earned the week before (D255). */
    goalOfLastWeek: z.number().gt(1),
    /** The value multiplier every member gets for the week after the goal is met. */
    bonusFactor: z.number().gt(1),
  })
  .strict();

export type IdleRegionContent = z.infer<typeof IdleRegionContentSchema>;

let cachedIdleRegion: IdleRegionContent | undefined;

/** Validated region goal numbers, loaded once per process. Throws on a bad file. */
export function getIdleRegionContent(): IdleRegionContent {
  cachedIdleRegion ??= IdleRegionContentSchema.parse(idleRegion);
  return cachedIdleRegion;
}
